import { useState, useEffect, useCallback, useRef } from 'react'
import { apiClient } from '@/services/api'
import { useErrorHandler } from '@/hooks/useAuth'
import { SortDirection as SortDirectionEnum } from '@/enums/sort-direction.enum'
import { ToolStatus } from '@/enums/tool-status.enum'
import type { Paginated, PaginationParams } from '@/types'

export interface TrolleySummary {
  id: number
  name: string
  description?: Nullable<string>
  location?: Nullable<string>
  is_active: boolean
  created_at: string
  updated_at: string
  quantity: number
  available_quantity: number
  borrowed_quantity: number
  missing_quantity: number
}

export interface GetTrolleySummaryQuery extends PaginationParams {
  search?: Nillable<string>
  status?: Nillable<ToolStatus>
  sort_by?: Nillable<string>
  sort_order?: Nillable<SortDirectionEnum>
}

type UseGetTrolleySummaryReturn = Paginated<TrolleySummary> & {
  isLoading: boolean
  error: Nullable<string>
  refetch: () => Promise<void>
}

export function useGetTrolleySummary(
  params?: GetTrolleySummaryQuery
): UseGetTrolleySummaryReturn {
  const [items, setItems] = useState<TrolleySummary[]>([])
  const [pagination, setPagination] = useState({
    page: params?.page ?? 1,
    page_size: params?.page_size ?? 10,
    total: 0,
    total_pages: 0,
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Nullable<string>>(null)
  const { handleError } = useErrorHandler()
  const handleErrorRef = useRef(handleError)

  useEffect(() => {
    handleErrorRef.current = handleError
  }, [handleError])

  const fetchSummary = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const search = params?.search?.trim() || undefined
      const status = params?.status?.trim() || undefined
      const sort_by = params?.sort_by?.trim() || undefined
      const sort_order = params?.sort_order || undefined
      const response = await apiClient.get<Paginated<TrolleySummary>>(
        '/trolleys/full/summary',
        {
          params: {
            page: params?.page ?? 1,
            page_size: params?.page_size ?? 10,
            ...(search ? { search } : {}),
            ...(status ? { status } : {}),
            ...(sort_by ? { sort_by } : {}),
            ...(sort_order ? { sort_order } : {}),
          },
        }
      )
      const data = response.data
      const itemsArray = Array.isArray(data.items) ? data.items : []
      setItems(itemsArray)
      setPagination({
        page: data.page,
        page_size: data.page_size,
        total: data.total,
        total_pages: data.total_pages,
      })
    } catch (err) {
      setError(handleErrorRef.current(err))
      setItems([])
      setPagination({
        page: params?.page ?? 1,
        page_size: params?.page_size ?? 10,
        total: 0,
        total_pages: 0,
      })
    } finally {
      setIsLoading(false)
    }
  }, [
    params?.page,
    params?.page_size,
    params?.search,
    params?.status,
    params?.sort_by,
    params?.sort_order,
  ])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  return {
    items,
    page: pagination.page,
    page_size: pagination.page_size,
    total: pagination.total,
    total_pages: pagination.total_pages,
    isLoading,
    error,
    refetch: fetchSummary,
  }
}
